import {
  Text,
  NextImage,
  withDatasourceCheck,
  Placeholder,
  TextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentWithContextProps } from 'lib/component-props';
import { LinkFieldValue, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import { useMemo } from 'react';
import classNames from 'classnames';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';

type ItemType = {
  displayName: string;
  fields: {
    cta: {
      value: LinkFieldValue;
    };
    id: string;
  };
  id: string;
  name: string;
  template: string;
};

type TileProps = ComponentWithContextProps & {
  fields: {
    id: string;
    image: ImageField;
    items: Array<ItemType>;
    description: TextField;
    title: TextField;
  };
  params: {
    background: string;
    size: string;
    showButton?: string;
  };
  singleTile?: boolean;
  insideArticleBody?: boolean;
  hardcodedBg?: string;
  containerSize?: string;
  className?: string;
};

const Tile = ({
  fields,
  singleTile = false,
  containerSize,
  insideArticleBody = false,
  hardcodedBg,
  params,
  rendering,
  className,
}: TileProps): JSX.Element => {
  const backgroundClass: string = hardcodedBg
    ? hardcodedBg
    : params.background || 'background-white';
  const isSvgIcon: boolean = fields.image.value?.['data-type'] === 'image/svg+xml';
  const size: string =
    containerSize && params?.size === 'default'
      ? containerSize
      : params?.size
      ? params.size
      : 'col-6';
  const tileImageBig: boolean = size === 'col-8' && !isSvgIcon;
  const tileImageMedium: boolean = size === 'col-6' && !isSvgIcon && !insideArticleBody;
  const articleTileImageMedium: boolean | undefined =
    insideArticleBody && !isSvgIcon && size === 'col-12';
  const articleTileImageSmall: boolean | undefined =
    insideArticleBody && !isSvgIcon && size === 'col-6';
  const tileImageSmall: boolean = size === 'col-4' && !isSvgIcon && !insideArticleBody;
  const tileImageExtraSmall: boolean = size === 'col-3' && !isSvgIcon && !insideArticleBody;
  const tileImageSVGSmall: boolean = isSvgIcon && size === 'col-4';
  const hasImage: boolean = !!fields.image.value && !!fields.image.value.src;
  // Correct typing for ctaLinks
  const ctaLinks: ItemType[] = fields.items || [];
  const tileWithButton: boolean = false; // TODO: update once Ben finish with implementation

  const tileWithLinks = ctaLinks.length > 1 || params.showButton !== '1';

  const outerVerticalPaddings = useMemo(() => {
    if (isSvgIcon) {
      if (insideArticleBody) {
        return 'py-3 pt-md-3 py-xl-3';
      }
      if (size !== 'col-4') {
        return 'py-3 pt-md-4 py-xl-4';
      }
      if (size === 'col-4') {
        return 'pt-3 pt-xl-7';
      }
    }
    if (!hasImage && !insideArticleBody) {
      if (size !== 'col-4') {
        return 'py-3 pt-md-4 pb-xl-4';
      }
      if (size === 'col-4') {
        return 'py-3';
      }
    }

    if (!hasImage && insideArticleBody) {
      return 'py-3 pt-md-3 pb-xl-3';
    }

    if (hasImage && !isSvgIcon) {
      if (size !== 'col-4') {
        return 'pb-xl-0';
      }
    }
    return '';
  }, [isSvgIcon, insideArticleBody, size, hasImage]);
  const innerVerticalPaddings = useMemo(() => {
    if (hasImage && !isSvgIcon) {
      if (size === 'col-3') {
        return 'pt-2 pt-md-3 pb-xl-3 pt-xl-3';
      } else if (size !== 'col-4' && !insideArticleBody) {
        return 'pt-2 pt-md-3 pb-xl-3 pt-xl-7';
      } else if (insideArticleBody) {
        return 'pt-3 mb-0 pb-xl-3';
      } else {
        return 'pt-2';
      }
    }
    return '';
  }, [hasImage, isSvgIcon, size, insideArticleBody]);

  const tileClassNames: string = classNames('tile', className, `${backgroundClass}-repeat`, {
    [`${size.split('-').join('-md-')}`]: !singleTile,
    'col-md-12': singleTile,
    [`${size}-article-tile`]: insideArticleBody,
  });

  const tileContentWrapperClassNames: string = classNames({
    [backgroundClass]: true,
    [outerVerticalPaddings]: outerVerticalPaddings,
    'flex-xl-column':
      size === 'col-4' || size === 'col-3' || isSvgIcon || (insideArticleBody && size === 'col-6'),
    'content-wrapper pb-3 d-flex flex-column flex-xl-row h-100 link-change': true,
  });

  const tileImageClassNames: string = classNames({
    'background-to-change': true,
    'tile-image-big': tileImageBig,
    'tile-image-medium': tileImageMedium,
    'article-tile-image-medium': articleTileImageMedium,
    'article-tile-image-small': articleTileImageSmall,
    'tile-image-small': tileImageSmall,
    'tile-image-extra-small': tileImageExtraSmall,
    'tile-image-svg': isSvgIcon,
    'tile-image-svg-small': tileImageSVGSmall,
  });

  return (
    <div className={tileClassNames}>
      <div className={tileContentWrapperClassNames}>
        <NextImage
          field={fields.image}
          className={tileImageClassNames}
          width={442}
          height={400}
          loading="eager"
        />
        <div className={`d-flex flex-column px-2 px-xl-3 h-100 w-100 ${innerVerticalPaddings}`}>
          <div>
            <Text
              field={fields.title}
              tag="h4"
              className={`background-to-change mb-2 ${
                isSvgIcon ? 'mt-3' : insideArticleBody ? 'mb-3' : ''
              }`}
            />
            <Text
              field={fields.description}
              tag="p"
              className="background-to-change body-default text-grey mb-1"
            />
            {rendering.placeholders && rendering.placeholders['abrdn-list'] && (
              <Placeholder
                name="abrdn-list"
                rendering={rendering}
                className="placeholder mt-2 mb-3"
              />
            )}
          </div>

          {tileWithLinks ? (
            <div className="mt-auto d-flex flex-column gap-2 align-items-start">
              {ctaLinks.map((field) => (
                <ScLink
                  key={field?.id}
                  field={field?.fields.cta}
                  className="background-to-change inline-link tile-link d-inline-block"
                />
              ))}
            </div>
          ) : (
            <div className="mt-auto d-flex flex-column align-items-start">
              <AbrdnButton
                buttonType="primary"
                className="mt-5 background-to-change w-100"
                linkField={ctaLinks[0]?.fields?.cta}
              />
            </div>
          )}
          {/*TODO:update once Ben finish with his task*/}
          {tileWithButton && <AbrdnButton buttonType="primary" className="mt-3" />}
        </div>
      </div>
    </div>
  );
};

export default withDatasourceCheck()<TileProps>(Tile);
